// Puzzle Grid Colors
export const PUZZLE_GRID_COLORS = {
    // Base colors
    WHITE: '#fff',
    TEXT_DARK: '#333',
    BORDER_LIGHT: '#ddd',
    HOVER_BACKGROUND: '#f5f5f5',
    BACKGROUND_LIGHT: '#fafafa',
    /** Visible outline only around the letter grid (not cell lines). Border width set on CrossGrid/GridGrid Paper. */
    GRID_OUTER_BORDER: '#7FB85A',
    
    // Cell state colors
    BLUE_SELECTED: '#2196f3',
    BLUE_SELECTED_HOVER: '#1976d2',
    YELLOW_CLICKED: '#FFEB3B',
    YELLOW_CLICKED_HOVER: '#FFC107',
    GREEN_FOUND: '#A5D6A7',
    GREEN_FOUND_HOVER: '#81C784',

    // Primary action button (Play Now, Browse, etc.)
    BUTTON_PRIMARY_GREEN: '#3BA666',
    BUTTON_PRIMARY_GREEN_HOVER: '#2e7d32',
    
    // Hidden message colors (modern vibrant style)
    HIDDEN_MESSAGE_BG: '#6366F1',           // Indigo-500 - modern vibrant color
    HIDDEN_MESSAGE_HOVER: '#4F46E5',      // Indigo-600 - darker on hover
    HIDDEN_MESSAGE_TEXT: '#FFFFFF',       // White text for contrast
    
    // Highlight colors (orange shades for typed word matching)
    ORANGE: '#FFA726',              // First character
    ORANGE_DARKER: '#FF9800',       // Second character
    ORANGE_DARKER_2: '#F57C00',     // Third character
    ORANGE_DARKEST: '#E65100',      // Fourth character
    ORANGE_VERY_DARK: '#BF360C',    // Fifth character
    ORANGE_DARKEST_SHADE: '#8D1B0D', // Sixth+ character
    
    // Highlight hover colors
    ORANGE_HOVER_1: '#FB8C00',
    ORANGE_HOVER_2: '#F57C00',
    ORANGE_HOVER_3: '#E65100',
    ORANGE_HOVER_4: '#BF360C',
    ORANGE_HOVER_5: '#8D1B0D',
    ORANGE_HOVER_6: '#6A0E0A'
};

const ColorService = {
    getStateColor: (state) => {
        const stateColors = {
            draft: "#9e9e9e",
            "ready_to_review": "#ff9800", 
            reviewed: "#2196f3",
            published: "#4caf50",
        };
        return stateColors[state?.toLowerCase()] || "#9e9e9e";
    },

    getDifficultyColor: (difficultyLevel) => {
        const difficultyColors = {
            easy: "#4caf50",
            medium: "#ff9800",
            hard: "#e57373",
            pro: "#1976d2",
            elite: "#7b1fa2",
            insane: "#4a148c",
        };
        return difficultyColors[difficultyLevel?.toLowerCase()] || "#9e9e9e";
    },

    getPricingColor: (free) => {
        return free === true ? "#4caf50" : "#ff9800";
    },
    
    // Get highlight color array for typed word matching
    getHighlightColors: () => {
        return [
            PUZZLE_GRID_COLORS.ORANGE,
            PUZZLE_GRID_COLORS.ORANGE_DARKER,
            PUZZLE_GRID_COLORS.ORANGE_DARKER_2,
            PUZZLE_GRID_COLORS.ORANGE_DARKEST,
            PUZZLE_GRID_COLORS.ORANGE_VERY_DARK,
            PUZZLE_GRID_COLORS.ORANGE_DARKEST_SHADE
        ];
    },
    
    // Get highlight hover colors map
    getHighlightHoverColors: () => {
        return {
            [PUZZLE_GRID_COLORS.ORANGE]: PUZZLE_GRID_COLORS.ORANGE_HOVER_1,
            [PUZZLE_GRID_COLORS.ORANGE_DARKER]: PUZZLE_GRID_COLORS.ORANGE_HOVER_2,
            [PUZZLE_GRID_COLORS.ORANGE_DARKER_2]: PUZZLE_GRID_COLORS.ORANGE_HOVER_3,
            [PUZZLE_GRID_COLORS.ORANGE_DARKEST]: PUZZLE_GRID_COLORS.ORANGE_HOVER_4,
            [PUZZLE_GRID_COLORS.ORANGE_VERY_DARK]: PUZZLE_GRID_COLORS.ORANGE_HOVER_5,
            [PUZZLE_GRID_COLORS.ORANGE_DARKEST_SHADE]: PUZZLE_GRID_COLORS.ORANGE_HOVER_6
        };
    },

    // Get professional gradient colors for genre
    getGenreGradient: (genre) => {
        if (!genre) {
            // Default gradient for General
            return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        }

        const genreName = (genre.parentGenre ? genre.parentGenre.name : genre.name).toLowerCase();
        
        // Professional gradient mappings for common genres
        const gradientMap = {
            'business': 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #7e8ba3 100%)',
            'politics': 'linear-gradient(135deg, #c94b4b 0%, #4b134f 100%)',
            'science': 'linear-gradient(135deg, #00c9ff 0%, #92fe9d 100%)',
            'technology': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            'sport': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            'lifestyle': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            'culture': 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            'lifestyle and culture': 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            'entertainment': 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
            'health': 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            'education': 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            'general': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        };

        // Check for exact match first
        if (gradientMap[genreName]) {
            return gradientMap[genreName];
        }

        // Check for partial matches
        for (const [key, gradient] of Object.entries(gradientMap)) {
            if (genreName.includes(key) || key.includes(genreName)) {
                return gradient;
            }
        }

        // Generate a consistent gradient based on genre name hash
        const hash = genreName.split('').reduce((acc, char) => {
            return char.charCodeAt(0) + ((acc << 5) - acc);
        }, 0);

        const gradients = [
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
            'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
            'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
        ];

        return gradients[Math.abs(hash) % gradients.length];
    }
};

export default ColorService;
